import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { AlertTriangle, CheckCircle, Clock, Users, UserCheck, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

interface AutomatedCheck {
  id: string;
  name: string;
  description: string;
  status: 'pending' | 'passed' | 'failed' | 'warning';
  required: boolean;
  details?: string;
  checkTime?: string;
}

interface ForfeitCondition {
  type: 'minimum_players' | 'roster_deadline' | 'readiness_deadline' | 'coach_approval';
  description: string;
  deadline?: string;
  currentValue: number;
  requiredValue: number;
  status: 'satisfied' | 'at_risk' | 'violated';
  gracePeriod?: number; // minutes
}

interface AutomatedChecksInterfaceProps {
  gameId: string;
  gameType: 'regular' | 'venue';
  gameData: any;
  onForfeitTriggered?: (reason: string, team: 'home' | 'away' | 'both') => void;
}

export const AutomatedChecksInterface: React.FC<AutomatedChecksInterfaceProps> = ({
  gameId,
  gameType,
  gameData,
  onForfeitTriggered
}) => {
  const [checks, setChecks] = useState<AutomatedCheck[]>([]);
  const [forfeitConditions, setForfeitConditions] = useState<ForfeitCondition[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastCheckTime, setLastCheckTime] = useState<string>('');

  useEffect(() => {
    runAutomatedChecks();
    
    // Run checks every 30 seconds
    const interval = setInterval(runAutomatedChecks, 30000);
    
    return () => clearInterval(interval);
  }, [gameId]);

  const runAutomatedChecks = async () => {
    try {
      setLoading(true);
      
      const gameStartTime = new Date(`${gameData.game_date} ${gameData.game_time}`);
      const now = new Date();
      const gameInProgress = now >= gameStartTime;
      const timeUntilGame = gameStartTime.getTime() - now.getTime();
      const hoursUntilGame = Math.max(0, timeUntilGame / (1000 * 60 * 60));

      // Initialize checks
      const newChecks: AutomatedCheck[] = [];
      const newForfeitConditions: ForfeitCondition[] = [];

      // 1. Minimum Players Check
      const { data: homeParticipants } = await supabase
        .from('game_participants')
        .select('*')
        .eq('game_id', gameId)
        .eq('team_side', 'home')
        .eq('participation_status', 'confirmed');

      const { data: awayParticipants } = await supabase
        .from('game_participants')
        .select('*')
        .eq('game_id', gameId)
        .eq('team_side', 'away')
        .eq('participation_status', 'confirmed');

      const homePlayerCount = homeParticipants?.length || 0;
      const awayPlayerCount = awayParticipants?.length || 0;
      const minPlayers = gameData.minimum_players_required || 2;

      newChecks.push({
        id: 'min_players_home',
        name: 'Home Team Minimum Players',
        description: `Home team must have at least ${minPlayers} confirmed players`,
        status: homePlayerCount >= minPlayers ? 'passed' : (hoursUntilGame <= 2 ? 'failed' : 'warning'),
        required: true,
        details: `${homePlayerCount}/${minPlayers} players confirmed`,
        checkTime: now.toISOString()
      });

      newChecks.push({
        id: 'min_players_away',
        name: 'Away Team Minimum Players',
        description: `Away team must have at least ${minPlayers} confirmed players`,
        status: awayPlayerCount >= minPlayers ? 'passed' : (hoursUntilGame <= 2 ? 'failed' : 'warning'),
        required: true,
        details: `${awayPlayerCount}/${minPlayers} players confirmed`,
        checkTime: now.toISOString()
      });

      // Add forfeit conditions
      newForfeitConditions.push({
        type: 'minimum_players',
        description: 'Home team minimum players',
        currentValue: homePlayerCount,
        requiredValue: minPlayers,
        status: homePlayerCount >= minPlayers ? 'satisfied' : (hoursUntilGame <= 1 ? 'violated' : 'at_risk'),
        gracePeriod: 5
      });

      newForfeitConditions.push({
        type: 'minimum_players',
        description: 'Away team minimum players',
        currentValue: awayPlayerCount,
        requiredValue: minPlayers,
        status: awayPlayerCount >= minPlayers ? 'satisfied' : (hoursUntilGame <= 1 ? 'violated' : 'at_risk'),
        gracePeriod: 5
      });

      // 2. Coach Approval Check
      const coachApprovalRequired = gameType === 'venue' || gameData.requires_coach_approval;
      if (coachApprovalRequired) {
        const { data: approvals } = await supabase
          .from('game_approval_hierarchy')
          .select('*')
          .eq('game_id', gameId)
          .eq('approver_role', 'coach')
          .eq('approval_status', 'approved');

        const hasCoachApproval = (approvals?.length || 0) > 0;

        newChecks.push({
          id: 'coach_approval',
          name: 'Coach Approval',
          description: 'Game must be approved by team coaches',
          status: hasCoachApproval ? 'passed' : (hoursUntilGame <= 1 ? 'failed' : 'warning'),
          required: true,
          details: hasCoachApproval ? 'Coach approval received' : 'Waiting for coach approval',
          checkTime: now.toISOString()
        });
      }

      // 3. Player Readiness Check - Use actual readiness status
      const { data: readyPlayers } = await supabase
        .from('player_readiness_status')
        .select('*')
        .eq('game_id', gameId)
        .eq('readiness_status', 'present_ready_to_play');

      const readyPlayerCount = readyPlayers?.length || 0;
      const totalRequiredPlayers = minPlayers * 2; // Both teams need minimum players

      newChecks.push({
        id: 'player_readiness',
        name: 'Player Readiness',
        description: 'Players must confirm they are ready to play',
        status: gameInProgress ? 
          (readyPlayerCount >= totalRequiredPlayers * 0.8 ? 'passed' : 'warning') : 
          'pending',
        required: false,
        details: `${readyPlayerCount}/${totalRequiredPlayers} players ready`,
        checkTime: now.toISOString()
      });

      // 4. Venue Confirmation
      if (gameType === 'venue') {
        const venueConfirmed = gameData.status === 'approved';
        
        newChecks.push({
          id: 'venue_confirmation',
          name: 'Venue Confirmation',
          description: 'Venue must confirm game booking',
          status: venueConfirmed ? 'passed' : (hoursUntilGame <= 4 ? 'failed' : 'warning'),
          required: true,
          details: venueConfirmed ? 'Venue confirmed' : 'Waiting for venue confirmation',
          checkTime: now.toISOString()
        });
      }

      // Equipment & Field Status removed - not all games have a venue

      // Check for automatic forfeit conditions
      if (gameInProgress || hoursUntilGame <= 0.1) { // Game started or within 6 minutes
        checkForfeitConditions(newForfeitConditions);
      }

      setChecks(newChecks);
      setForfeitConditions(newForfeitConditions);
      setLastCheckTime(now.toLocaleTimeString());

    } catch (error) {
      console.error('Error running automated checks:', error);
      toast.error('Failed to run automated checks');
    } finally {
      setLoading(false);
    }
  };

  const checkForfeitConditions = (conditions: ForfeitCondition[]) => {
    const violatedConditions = conditions.filter(c => c.status === 'violated');
    
    if (violatedConditions.length > 0) {
      violatedConditions.forEach(condition => {
        const isHomeTeam = condition.description.includes('Home');
        const isAwayTeam = condition.description.includes('Away');
        
        if (isHomeTeam && isAwayTeam) {
          onForfeitTriggered?.('Both teams failed to meet minimum requirements', 'both');
        } else if (isHomeTeam) {
          onForfeitTriggered?.(`Home team: ${condition.description}`, 'home');
        } else if (isAwayTeam) {
          onForfeitTriggered?.(`Away team: ${condition.description}`, 'away');
        } else {
          onForfeitTriggered?.(condition.description, 'both');
        }
      });
    }
  };

  const getCheckIcon = (status: string) => {
    switch (status) {
      case 'passed':
        return (
          <div className="rounded-full bg-green-100 dark:bg-green-900/30 p-1.5">
            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
          </div>
        );
      case 'failed':
        return (
          <div className="rounded-full bg-red-100 dark:bg-red-900/30 p-1.5">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
          </div>
        );
      case 'warning':
        return (
          <div className="rounded-full bg-amber-100 dark:bg-amber-900/30 p-1.5">
            <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
        );
      default:
        return (
          <div className="rounded-full bg-gray-100 dark:bg-gray-800 p-1.5">
            <Clock className="h-5 w-5 text-muted-foreground" />
          </div>
        );
    }
  };

  const getCheckBadge = (status: string, required: boolean) => {
    const getClass = () => {
      if (status === 'passed') return 'bg-green-600 text-white hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700';
      if (status === 'failed') return 'bg-red-600 text-white hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700';
      if (status === 'warning') return 'bg-amber-600 text-white hover:bg-amber-700 dark:bg-amber-600 dark:hover:bg-amber-700';
      return 'bg-gray-500 text-white hover:bg-gray-600 dark:bg-gray-500 dark:hover:bg-gray-600';
    };
    
    return (
      <Badge className={`${getClass()} text-xs font-semibold px-3 py-1`}>
        {status.toUpperCase()} {required && '✱'}
      </Badge>
    );
  };

  const getForfeitStatusColor = (status: string) => {
    switch (status) {
      case 'satisfied': return 'text-green-600';
      case 'at_risk': return 'text-yellow-600';
      case 'violated': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getOverallStatus = () => {
    const requiredChecks = checks.filter(c => c.required);
    const passedRequired = requiredChecks.filter(c => c.status === 'passed').length;
    const failedRequired = requiredChecks.filter(c => c.status === 'failed').length;
    
    if (failedRequired > 0) return 'critical';
    if (passedRequired === requiredChecks.length) return 'ready';
    return 'pending';
  };

  const overallStatus = getOverallStatus();
  const progressPercentage = checks.length > 0 ? 
    (checks.filter(c => c.status === 'passed').length / checks.length) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Overall Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5" />
            Game Readiness Status
          </CardTitle>
          <CardDescription>
            Automated checks and forfeit monitoring
            {lastCheckTime && ` • Last checked: ${lastCheckTime}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Overall Progress</span>
                <Badge variant={
                  overallStatus === 'ready' ? 'default' : 
                  overallStatus === 'critical' ? 'destructive' : 'secondary'
                }>
                  {overallStatus.toUpperCase()}
                </Badge>
              </div>
              <Progress value={progressPercentage} className="h-2" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Automated Checks */}
      <Card>
        <CardHeader>
          <CardTitle>Automated Checks</CardTitle>
          <CardDescription>
            System-monitored requirements for game completion
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {checks.map((check) => (
              <div key={check.id} className="flex items-start justify-between gap-4 p-5 bg-muted/40 rounded-lg border border-border/50 hover:bg-muted/60 transition-colors">
                <div className="flex items-start gap-4 flex-1">
                  {getCheckIcon(check.status)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                      <h4 className="font-semibold text-base">{check.name}</h4>
                      {getCheckBadge(check.status, check.required)}
                    </div>
                    {check.description && (
                      <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{check.description}</p>
                    )}
                    {check.details && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-foreground bg-muted px-3 py-2 rounded inline-block border border-border">
                          {check.details}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Forfeit Conditions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Forfeit Monitoring
          </CardTitle>
          <CardDescription>
            Both teams must meet minimum requirements to avoid forfeit
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Team-by-team view */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {forfeitConditions.filter(c => c.type === 'minimum_players').map((condition, idx) => {
                const isHome = condition.description.toLowerCase().includes('home');
                const teamLabel = isHome ? 'Home Team' : 'Away Team';
                const statusColor = condition.status === 'satisfied' ? 'border-green-500 dark:border-green-600' : 
                                  condition.status === 'violated' ? 'border-red-500 dark:border-red-600' : 
                                  'border-amber-500 dark:border-amber-600';
                
                return (
                  <div key={idx} className={`p-4 bg-muted/40 rounded-lg border-2 ${statusColor}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-base">{teamLabel}</h4>
                      <Badge 
                        className={
                          condition.status === 'satisfied' ? 'bg-green-600 text-white hover:bg-green-700' :
                          condition.status === 'violated' ? 'bg-red-600 text-white hover:bg-red-700' :
                          'bg-amber-600 text-white hover:bg-amber-700'
                        }
                      >
                        {condition.status === 'satisfied' ? 'MET' : condition.status === 'violated' ? 'FAILED' : 'PENDING'}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Players Confirmed:</span>
                        <span className="font-semibold text-base">{condition.currentValue} / {condition.requiredValue}</span>
                      </div>
                      <div className="w-full bg-muted/60 h-2.5 rounded-full overflow-hidden">
                        <div 
                          className="h-full transition-all duration-500"
                          style={{
                            width: `${Math.min((condition.currentValue / condition.requiredValue) * 100, 100)}%`,
                            backgroundColor: condition.status === 'satisfied' ? 
                              'hsl(142.1 76.2% 36.3%)' : 
                              'hsl(38.8 92.1% 50.2%)'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Other forfeit conditions */}
            {forfeitConditions.filter(c => c.type !== 'minimum_players').length > 0 && (
              <div className="space-y-3 mt-4 pt-4 border-t">
                <h4 className="text-sm font-semibold">Additional Requirements</h4>
                {forfeitConditions.filter(c => c.type !== 'minimum_players').map((condition, idx) => (
                  <div key={idx} className="flex items-start justify-between gap-4 p-4 bg-muted/40 rounded-lg border">
                    <div className="flex-1">
                      <p className="font-medium text-sm mb-2">{condition.description}</p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className={getForfeitStatusColor(condition.status)}>
                          {condition.status === 'satisfied' ? '✓ Requirement Met' : 
                           condition.status === 'violated' ? '✗ Requirement Failed' : 
                           '○ Pending'}
                        </span>
                      </div>
                    </div>
                    <Badge 
                      className={
                        condition.status === 'satisfied' ? 'bg-green-600 text-white' :
                        condition.status === 'violated' ? 'bg-red-600 text-white' :
                        'bg-gray-500 text-white'
                      }
                    >
                      {condition.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Manual Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Manual Controls</CardTitle>
          <CardDescription>
            Emergency actions and manual overrides
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={runAutomatedChecks}
              disabled={loading}
            >
              {loading ? 'Checking...' : 'Refresh Checks'}
            </Button>
            <Button variant="destructive" size="sm">
              Report Emergency
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};